import React, { useEffect, useRef, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { AUTH_REDIRECT } from '../../utils/apiUtils';
import OcrSourcePreview from '../OcrHub/OcrSourcePreview';
import { getSingleOcr } from './singleOcrApi';
import { validBbox } from './singleOcrInput';

/** 목록에는 이미지 URL이 없으므로 보이는 작업만 상세를 읽는다. 작업 실행은 하지 않는다. */
export default function SingleOcrTaskPreview({ item, disabled, onOpen }) {
  const { getAccessToken } = useAuth();
  const frameRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') { setVisible(true); return undefined; }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) { setVisible(true); observer.disconnect(); }
    }, { root: frameRef.current.closest('[data-single-ocr-list]'), rootMargin: '80px' });
    observer.observe(frameRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    let active = true;
    setPreview(null); setError(false);
    getSingleOcr(item.single_id, getAccessToken).then((data) => {
      if (!active || data === AUTH_REDIRECT) return;
      const pages = data.pages || [];
      const source = data.problem?.sources?.find((s) => s.is_body);
      const sourcePage = pages.find((p) => String(p.page_id) === String(source?.page_id));
      const page = sourcePage || pages.find((p) => p.page_index === 0) || pages[0];
      if (!page?.image_url) { setError(true); return; }
      setPreview({ page, count: pages.length, bbox: sourcePage && validBbox(source?.bbox) ? source.bbox : null });
    }).catch(() => { if (active) setError(true); });
    return () => { active = false; };
  }, [visible, item.single_id, item.updated_at, getAccessToken, retry]);

  const bbox = preview?.bbox;
  return <div ref={frameRef} className="w-28 shrink-0">
    <button type="button" onClick={onOpen} disabled={disabled}
      aria-label={`${item.name} 미리보기로 ${item.state === 'saved' ? '문제 보기' : '이어 하기'}`}
      className="relative flex h-24 w-full overflow-hidden rounded-lg border border-gray-200 bg-gray-50 p-1 transition-colors hover:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50">
      {error ? <span className="flex flex-1 flex-col items-center justify-center gap-1 text-[11px] text-gray-500"><ImageOff className="size-5" aria-hidden="true" />미리보기 없음</span>
        : preview ? <OcrSourcePreview key={preview.page.image_url} src={preview.page.image_url} alt={`${item.name} 원본 미리보기`} onError={() => setError(true)}>
          {bbox && <span aria-hidden="true" className="pointer-events-none absolute border border-primary bg-primary/10" style={{ left: `${bbox[0] * 100}%`, top: `${bbox[1] * 100}%`, width: `${(bbox[2] - bbox[0]) * 100}%`, height: `${(bbox[3] - bbox[1]) * 100}%` }} />}
        </OcrSourcePreview>
          : <span className="flex flex-1 items-center justify-center text-gray-400"><Loader2 className="size-4 animate-spin" aria-hidden="true" /><span className="sr-only">미리보기 불러오는 중</span></span>}
    </button>
    {error ? <button type="button" disabled={disabled} onClick={() => setRetry((value) => value + 1)} aria-label={`${item.name} 미리보기 다시 불러오기`} className="mt-1 w-full rounded text-center text-[11px] text-gray-500 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50">다시 불러오기</button>
      : preview?.count > 1 && <p className="mt-1 text-center text-[11px] text-gray-500">{preview.page.page_index + 1} / {preview.count}쪽</p>}
  </div>;
}
